(() => {
  'use strict';

  const STORAGE_KEY = 'jmo-wishlist-v1';
  const MAX_ITEMS = 100;
  const cleanText = (value, limit) => typeof value === 'string' ? value.trim().slice(0, limit) : '';

  function safeProductUrl(value, origin) {
    try {
      const url = new URL(value, origin);
      if (url.origin !== origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return '';
      const variant = url.searchParams.get('variant');
      return url.pathname + (variant && /^\d+$/.test(variant) ? '?variant=' + variant : '');
    } catch (_) { return ''; }
  }

  function safeImageUrl(value, origin) {
    if (!value || typeof value !== 'string') return '';
    try {
      const url = new URL(value, origin);
      if (url.protocol !== 'https:' || (url.origin !== origin && url.hostname !== 'cdn.shopify.com')) return '';
      url.searchParams.set('width', '320');
      return url.href;
    } catch (_) { return ''; }
  }

  function normalizeItem(value, origin) {
    if (!value || typeof value !== 'object') return null;
    const id = String(value.id || '');
    const title = cleanText(value.title, 240);
    const url = safeProductUrl(value.url, origin);
    if (!/^\d+$/.test(id) || !title || !url) return null;
    return {
      id, title, url,
      image: safeImageUrl(value.image, origin),
      variantId: /^\d+$/.test(String(value.variantId || '')) ? String(value.variantId) : '',
      variantTitle: cleanText(value.variantTitle, 160),
      priceText: cleanText(value.priceText, 100),
      currency: cleanText(value.currency, 5),
      savedAt: cleanText(value.savedAt, 40)
    };
  }

  function readItems(raw, origin) {
    if (raw === null) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.items)) throw new Error('Invalid wishlist data');
    const items = [];
    const ids = new Set();
    parsed.items.slice(0, MAX_ITEMS).forEach(value => {
      const item = normalizeItem(value, origin);
      if (item && !ids.has(item.id)) { items.push(item); ids.add(item.id); }
    });
    return items;
  }

  function createStore(storage, origin) {
    let items = [];
    let persistent = Boolean(storage);
    let warning = '';
    if (storage) {
      let raw = null;
      try { raw = storage.getItem(STORAGE_KEY); } catch (_) { persistent = false; }
      if (persistent) {
        try { items = readItems(raw, origin); }
        catch (_) { warning = 'Your previous wishlist could not be read. You can start a new list.'; }
      }
    }
    function persist() {
      warning = '';
      if (!persistent) return;
      try { storage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, items })); }
      catch (_) { persistent = false; }
    }
    return {
      items: () => items.slice(),
      persistent: () => persistent,
      warning: () => warning,
      has: id => items.some(item => item.id === String(id)),
      toggle(value) {
        const item = normalizeItem(value, origin);
        if (!item) return { error: 'This piece could not be saved. Please reload the product page and try again.' };
        const index = items.findIndex(saved => saved.id === item.id);
        if (index >= 0) {
          items.splice(index, 1); persist(); return { added: false, item };
        }
        if (items.length >= MAX_ITEMS) return { error: 'Your wishlist has 100 pieces. Remove one before saving another.' };
        items.unshift(item); persist(); return { added: true, item };
      },
      remove(id) {
        const previous = items.length;
        items = items.filter(item => item.id !== String(id));
        if (items.length !== previous) persist();
        return previous !== items.length;
      },
      sync(raw) {
        if (!persistent) return false;
        try { items = readItems(raw, origin); warning = ''; return true; }
        catch (_) { warning = 'A wishlist update could not be read. Your current list is still available.'; return false; }
      }
    };
  }

  // Read the native variant selector at the time of saving, not the initial Liquid selection.
  function productSnapshot(button, origin) {
    const holder = button.closest('[data-jmo-wishlist-product]');
    const source = holder && holder.querySelector('[data-jmo-wishlist-product-json]');
    if (!source) return null;
    let value;
    try { value = JSON.parse(source.textContent); } catch (_) { return null; }
    const product = button.closest('[data-jmo-product]');
    if (product) {
      const selector = product.querySelector('[data-jmo-variant-select]');
      if (selector) {
        let data;
        try { data = JSON.parse(product.querySelector('[data-jmo-product-json]').textContent); } catch (_) { return null; }
        if (!Array.isArray(data.variants)) return null;
        const variant = !selector.disabled && data.variants.find(item => String(item.id) === String(selector.value));
        const baseUrl = safeProductUrl(data.productUrl || value.url, origin).split('?')[0];
        if (!baseUrl) return null;
        value.url = baseUrl;
        value.variantId = '';
        value.variantTitle = '';
        value.priceText = 'Choose options to view price';
        if (variant) {
          value.url += '?variant=' + variant.id;
          value.variantId = String(variant.id);
          value.variantTitle = Array.isArray(data.optionNames) && data.optionNames.length ? variant.options.join(' / ') : '';
          const price = product.querySelector('[data-jmo-price]');
          value.priceText = price ? price.textContent.replace(/\s+/g, ' ').trim() : variant.priceText;
          const media = /^\d+$/.test(String(variant.mediaId)) && product.querySelector('[data-media-id="' + variant.mediaId + '"] img');
          if (media) value.image = media.currentSrc || media.src;
        }
      }
    }
    value.savedAt = new Date().toISOString();
    return normalizeItem(value, origin);
  }

  function initialize() {
    const dialog = document.querySelector('[data-jmo-wishlist-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.dataset.wishlistReady) return;
    const list = dialog.querySelector('[data-jmo-wishlist-items]');
    const empty = dialog.querySelector('[data-jmo-wishlist-empty]');
    const note = dialog.querySelector('[data-jmo-wishlist-storage]');
    const message = dialog.querySelector('[data-jmo-wishlist-message]');
    const closeButton = dialog.querySelector('[data-jmo-wishlist-close]');
    const announcement = document.querySelector('[data-jmo-wishlist-announcement]');
    if (!list || !empty || !note || !message || !closeButton) return;
    dialog.dataset.wishlistReady = 'true';
    let storage;
    try { storage = window.localStorage; } catch (_) { storage = null; }
    const store = createStore(storage, window.location.origin);
    let opener = null;
    let restoreFocus = true;

    function announce(text) {
      if (announcement) announcement.textContent = text;
      message.textContent = text;
      message.hidden = !text;
    }

    function element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text) node.textContent = text;
      return node;
    }

    function render() {
      const items = store.items();
      list.replaceChildren();
      items.forEach(item => {
        const row = element('li', 'jmo-wishlist-item');
        const imageLink = element('a', 'jmo-wishlist-image');
        imageLink.href = item.url;
        imageLink.tabIndex = -1;
        imageLink.setAttribute('aria-hidden', 'true');
        if (item.image) {
          const img = element('img');
          img.src = item.image; img.alt = ''; img.width = 88; img.height = 110; img.loading = 'lazy';
          img.addEventListener('error', () => { imageLink.textContent = 'View piece'; }, { once: true });
          imageLink.append(img);
        } else imageLink.textContent = 'View piece';
        const details = element('div');
        const heading = element('h3');
        const link = element('a', '', item.title); link.href = item.url; heading.append(link);
        details.append(heading);
        if (item.variantTitle) details.append(element('p', 'jmo-wishlist-variant', item.variantTitle));
        if (item.priceText) details.append(element('p', 'jmo-wishlist-price', 'Price when saved: ' + item.priceText));
        const remove = element('button', 'jmo-wishlist-remove', 'Remove');
        remove.type = 'button'; remove.dataset.jmoWishlistRemove = item.id;
        remove.setAttribute('aria-label', 'Remove ' + item.title + ' from wishlist');
        details.append(remove); row.append(imageLink, details); list.append(row);
      });
      list.hidden = !items.length;
      empty.hidden = Boolean(items.length);
      note.textContent = store.persistent() ? 'Saved on this browser.' : 'Saved for this visit only. Browser storage isn’t available.';
      document.querySelectorAll('[data-jmo-wishlist-count]').forEach(count => { count.textContent = String(items.length); count.hidden = !items.length; });
      document.querySelectorAll('[data-jmo-wishlist-open]').forEach(button => {
        button.disabled = false;
        button.setAttribute('aria-disabled', 'false');
        button.setAttribute('aria-controls', dialog.id);
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-label', items.length ? 'Open wishlist, ' + items.length + (items.length === 1 ? ' saved piece' : ' saved pieces') : 'Open wishlist');
      });
      document.querySelectorAll('[data-jmo-wishlist-save]').forEach(button => {
        const holder = button.closest('[data-jmo-wishlist-product]');
        let data;
        try { data = JSON.parse(holder.querySelector('[data-jmo-wishlist-product-json]').textContent); } catch (_) { return; }
        if (!normalizeItem(data, window.location.origin)) return;
        const saved = store.has(data.id);
        button.disabled = false; button.hidden = false;
        button.setAttribute('aria-pressed', String(saved));
        button.setAttribute('aria-label', (saved ? 'Remove ' : 'Save ') + data.title + (saved ? ' from wishlist' : ' to wishlist'));
        const label = button.querySelector('[data-jmo-wishlist-save-label]');
        if (label) label.textContent = saved ? 'Saved to wishlist' : 'Save to wishlist';
      });
    }

    function open(button) {
      if (dialog.open) return;
      if (document.querySelector('dialog[open]')) { announce('Close the current window before opening your wishlist.'); return; }
      document.querySelectorAll('[data-header-details][open]').forEach(details => { details.open = false; });
      document.body.classList.remove('jmo-panel-open');
      opener = button;
      restoreFocus = true;
      render();
      announce(store.warning());
      try {
        dialog.showModal();
        document.documentElement.classList.add('jmo-wishlist-is-open');
        closeButton.focus();
      } catch (_) { announce('Your wishlist could not open. Please reload this page and try again.'); }
    }

    document.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest('[data-jmo-wishlist-open]');
      if (trigger && !trigger.disabled) { event.preventDefault(); open(trigger); return; }
      const save = event.target.closest('[data-jmo-wishlist-save]');
      if (save && !save.disabled) {
        event.preventDefault();
        const result = store.toggle(productSnapshot(save, window.location.origin));
        render();
        announce(result.error || result.item.title + (result.added ? ' saved to your wishlist.' : ' removed from your wishlist.') + (!store.persistent() ? ' Saved for this visit only.' : ''));
        return;
      }
      const remove = event.target.closest('[data-jmo-wishlist-remove]');
      if (remove && dialog.contains(remove)) {
        const buttons = Array.from(list.querySelectorAll('[data-jmo-wishlist-remove]'));
        const index = buttons.indexOf(remove);
        store.remove(remove.dataset.jmoWishlistRemove); render(); announce('Piece removed from your wishlist.');
        const remaining = list.querySelectorAll('[data-jmo-wishlist-remove]');
        const next = remaining[Math.min(index, remaining.length - 1)] || dialog.querySelector('[data-jmo-wishlist-empty-link]') || closeButton;
        next.focus();
      }
      if (event.target.closest('[data-jmo-wishlist-close]') && dialog.open) dialog.close();
    });
    dialog.addEventListener('cancel', event => { event.preventDefault(); dialog.close(); });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('jmo-wishlist-is-open');
      if (restoreFocus && opener && opener.isConnected && !opener.disabled) opener.focus();
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialog.querySelectorAll('a[href],button:not(:disabled),[tabindex="0"]')).filter(node => node.getClientRects().length && node.tabIndex !== -1);
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    window.addEventListener('storage', event => {
      if ((event.key === STORAGE_KEY || event.key === null) && (!event.storageArea || event.storageArea === storage)) {
        if (store.sync(event.key === null ? null : event.newValue)) { render(); announce('Wishlist updated on this browser.'); }
      }
    });
    window.addEventListener('pagehide', () => { restoreFocus = false; if (dialog.open) dialog.close(); });
    window.addEventListener('pageshow', () => { render(); });
    document.addEventListener('shopify:section:load', render);
    render();
    if (store.warning()) announce(store.warning());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
